import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as HttpStatus from 'http-status-codes';
import { PayPalEcStatesEnum } from 'i2g-ng-payment';
import { List, Map } from 'immutable';
import { isEqual, isUndefined } from 'lodash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  finalize,
  flatMap,
  map,
  shareReplay,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { isBoolean } from 'util';
import { Account } from '../../../account-data/account.model';
import { AttachmentView } from '../../../attachments/attachment-view.model';
import { Attachment } from '../../../attachments/attachment.model';
import { AttachmentsStore } from '../../../attachments/attachments.store';
import { CLIENTS_JOBS_ROUTE } from '../../../client-data/client-data.data';
import { ClientDataDispatcher } from '../../../client-data/client-data.dispatcher';
import { Client } from '../../../client-data/client.model';
import { IConfirmDialogResult } from '../../../dialog/components/confirm-dialog/confirm-dialog.interface';
import { DialogActionsEnum, IDialogTrigger } from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import { IDocumentDataDispatcher } from '../../../document-data/document-data-dispatcher.interface';
import {
  ADD_CLIENT_QUERY_PARAM,
  ADD_JOB_QUERY_PARAM,
  DocumentOverallStatesEnum,
  DocumentTypesEnum,
} from '../../../document-data/document-data.data';
import { IDocumentStore } from '../../../document-data/document-store.interface';
import { Document } from '../../../document-data/document.model';
import { Billing } from '../../../document-data/sub-models/billing.model';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { Deposit } from '../../../document-data/sub-models/deposit.model';
import { DocumentPayments } from '../../../document-data/sub-models/document-payments.model';
import { DocumentPreview } from '../../../document-data/sub-models/document-preview.model';
import { Schedule } from '../../../document-data/sub-models/schedule.model';
import { States } from '../../../document-data/sub-models/states.model';
import {
  IFeaturesDataResolveData,
  RESOLVE_KEY as featureDataResolveKey,
} from '../../../features-data/features-data.data';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { InvoiceDataDispatcher } from '../../../invoice-data/invoice-data.dispatcher';
import { InvoiceDataStore } from '../../../invoice-data/invoice-data.store';
import {
  INVOICES_PAID_ROUTE,
  INVOICES_UNPAID_ROUTE,
} from '../../../invoices/invoices.data';
import { JobDataDispatcher } from '../../../job-data/job-data.dispatcher';
import { CLIENTS_MASTER_ROUTE } from '../../../main/main.data';
import { DIALOG_TYPE as sendingDialog } from '../../../message-delivery/components/sending-dialog/sending-dialog.component';
import { IMessageDeliveryData } from '../../../message-delivery/message-delivery.interface';
import { NavigationService } from '../../../navigation/navigation.service';
import { DIALOG_TYPE as pdfRerenderDialog } from '../../../renderer/components/pdf-rerender-dialog/pdf-rerender-dialog.component';
import { I2gMoney } from '../../../settings-data/i2g-money.data';
import { SettingsDataDispatcher } from '../../../settings-data/settings-data.dispatcher';
import { SettingsDataStore } from '../../../settings-data/settings-data.store';
import { Settings } from '../../../settings-data/settings.model';
import { CompanyInfo } from '../../../settings-data/sub-models/company-info.model';
import { CompanySettings } from '../../../settings-data/sub-models/company-settings/company-settings.model';
import { DepositToggleValue } from '../../../settings-data/sub-models/company-settings/deposit-toggle-value.model';
import { EmailDefaults } from '../../../settings-data/sub-models/company-settings/email-defaults.model';
import { PaymentReminders } from '../../../settings-data/sub-models/company-settings/payment-reminders.model';
import { PaymentsSettings } from '../../../settings-data/sub-models/company-settings/payments.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { GlobalBannerService } from '../../../shared/components/global-banner/global-banner.service';
import { ERRORS } from '../../../shared/constants/errors.constant';
import { EmailService } from '../../../shared/services/email/email.service';
import { UtilsService } from '../../../shared/services/utils/utils.service';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { DocumentDetailService } from '../../document-detail.service';
import { DIALOG_TYPE as depositRequestDialog } from '../deposit-request-dialog/deposit-request-dialog.component';
import { DIALOG_TYPE as updateClientEmailDialog } from '../update-client-email-dialog/update-client-email-dialog.component';
import { DIALOG_TYPE as updateDepositRequestDialog } from '../update-deposit-request-dialog/update-deposit-request-dialog.component';
import { DocumentSendBuilder } from './document-send.builder';

@Component({
  selector: 'app-document-send',
  templateUrl: './document-send.component.html',
  styleUrls: ['./document-send.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [DocumentSendBuilder],
})
export class DocumentSendComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() documentDataDispatcher: IDocumentDataDispatcher;
  @Input() documentDataStore: IDocumentStore;
  @Input() masterRoute: string;
  @Input() useOutstandingBalance: boolean;
  @Input() showPaid: boolean;
  @Input() showTotal: boolean;
  @Input() enablePayments = false;
  @Input() enableBankTransfer: boolean;
  @Input() enableCardPayments: boolean;
  @Input() enablePaypal: boolean;
  @Input() enableSchedules: boolean;
  @Input() enableDeposits: boolean;
  @Input() showPaymentActions: boolean;
  @Input() showPaymentReminders: boolean;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() depositRequestFeatureName: string;
  @Input() featureToggles: Toggles;
  @Input() featurePayloads: Map<string, any>;
  @Input() account: Account;

  private _messageDeliveryData: IMessageDeliveryData;
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  disableSendButton: boolean;
  documentHasUnsavedChanges$: Observable<boolean>;
  companyInfo$: Observable<CompanyInfo>;
  documentBilling$: Observable<Billing>;
  documentCalculationResult$: Observable<CalculationResult>;
  documentDate$: Observable<string>;
  documentDueDate$: Observable<string>;
  documentPaymentUrl$: Observable<string>;
  documentNumber$: Observable<string>;
  documentOverallState$: Observable<string>;
  documentPayments$: Observable<DocumentPayments>;
  documentSchedule$: Observable<Schedule>;
  documentSettings$: Observable<DocumentPresetSettings>;
  documentLocale$: Observable<string>;
  documentState$: Observable<States>;
  documentRevisionId$: Observable<string>;
  sourceDocumentPreview$: Observable<DocumentPreview>;
  linkedDocumentPreview$: Observable<DocumentPreview>;
  documentType$: Observable<DocumentTypesEnum>;
  documentFeatureName$: Observable<string>;
  emailDefaults$: Observable<EmailDefaults>;
  underwritingUrl$: Observable<string>;
  companySettings$: Observable<CompanySettings>;
  paymentsSettings$: Observable<PaymentsSettings>;
  remindersSettings$: Observable<PaymentReminders>;
  documentId$: Observable<string>;
  documentTrackingObject$: Observable<ITrackingObject>;
  deposit$: Observable<Deposit>;
  addDepositForFuture$: Observable<DepositToggleValue>;
  document$: Observable<Document>;
  clientName$: Observable<string>;
  showI2gMoney: boolean;
  showBankTransfer$: Observable<boolean>;
  showCardPayments$: Observable<boolean>;
  showPaypalEc$: Observable<boolean>;
  i2gMoneyEnabled$: Observable<boolean>;
  bankTransferEnabled$: Observable<boolean>;
  cardPaymentsEnabled$: Observable<boolean>;
  paypalEcEnabled$: Observable<boolean>;
  paypalEcStatus$: Observable<PayPalEcStatesEnum>;
  pdfSize$: Observable<number>;
  attachments$: Observable<List<AttachmentView>>;
  fileAttachment$: Observable<List<Attachment>>;
  jobId: string;
  clientId: string;
  toggles: Toggles;

  constructor(
    private _settingsDataStore: SettingsDataStore,
    private _settingsDataDispatcher: SettingsDataDispatcher,
    private _trackingDispatcher: TrackingDispatcher,
    private _clientDataDispatcher: ClientDataDispatcher,
    private _globalBannerService: GlobalBannerService,
    private _navigationService: NavigationService,
    private _dialogService: DialogService,
    private _emailService: EmailService,
    private _utilsService: UtilsService,
    private _documentSendBuilder: DocumentSendBuilder,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _changeDetectorRef: ChangeDetectorRef,
    private _attachmentsStore: AttachmentsStore,
    private _route: ActivatedRoute,
    private _jobDataDispatcher: JobDataDispatcher,
    private _documentDetailService: DocumentDetailService
  ) {}

  ngOnInit() {
    const routeSnapshot = this._route.snapshot;
    const queryParamMap = routeSnapshot.queryParamMap;
    this.jobId = queryParamMap.get(ADD_JOB_QUERY_PARAM);
    this.clientId = queryParamMap.get(ADD_CLIENT_QUERY_PARAM);

    const { toggles }: IFeaturesDataResolveData = routeSnapshot.data[
      featureDataResolveKey
    ];

    const validDocument$: Observable<Document> = this.documentDataStore.getValidEditingEntity();

    this.toggles = toggles;
    this.disableSendButton = true;

    this.documentSchedule$ =
      this.documentDataStore instanceof InvoiceDataStore
        ? validDocument$.pipe(
            flatMap((document: Document) =>
              (this
                .documentDataStore as InvoiceDataStore).getSchedulesByInvoiceId(
                document.id
              )
            ),
            distinctUntilChanged((a, b) => a && a.equals(b))
          )
        : of(null);

    this.documentHasUnsavedChanges$ = combineLatest([
      this.documentDataStore.hasEditingEntityValueChanged(),
      this.documentDataStore.hasEditingEntityBeenSaved(),
    ]).pipe(
      map(
        ([hasChanges, hasBeenSaved]: boolean[]) => hasChanges && !hasBeenSaved
      )
    );

    this.document$ = validDocument$;

    this.documentId$ = validDocument$.pipe(
      map((doc: Document) => doc.id),
      take(1)
    );

    this.pdfSize$ = validDocument$.pipe(
      map((doc: Document) => doc.pdfSize),
      take(1)
    );

    this.documentType$ = validDocument$.pipe(
      map((doc: Document) => doc.documentType),
      take(1),
      shareReplay(1)
    );

    this.documentFeatureName$ = this.documentType$.pipe(
      map((documentType: DocumentTypesEnum) => {
        switch (documentType) {
          case DocumentTypesEnum.invoice:
            return Toggles.KEYS.INVOICES;
          case DocumentTypesEnum.estimate:
            return Toggles.KEYS.ESTIMATE;
          case DocumentTypesEnum.credit_memo:
            return Toggles.KEYS.CREDIT_MEMO;
          case DocumentTypesEnum.purchase_order:
            return Toggles.KEYS.PURCHASE_ORDER;
          default:
            return null;
        }
      }),
      filter((x) => !!x),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.documentSettings$ = validDocument$.pipe(
      map((doc: Document) => doc.settings),
      distinctUntilChanged((a, b) => a && a.equals(b)),
      shareReplay(1)
    );

    this.documentState$ = validDocument$.pipe(
      map((doc: Document) => doc.states),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.companyInfo$ = validDocument$.pipe(
      map((doc: Document) => doc.companyInfo),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentBilling$ = validDocument$.pipe(
      map((doc: Document) => doc.billing),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentCalculationResult$ = validDocument$.pipe(
      map((doc: Document) => doc.calculationResult),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentDate$ = validDocument$.pipe(
      map((doc: Document) => doc.docDate),
      distinctUntilChanged()
    );

    this.documentDueDate$ = validDocument$.pipe(
      map((doc: Document) => doc.dueDate),
      distinctUntilChanged()
    );

    this.documentNumber$ = validDocument$.pipe(
      map((doc: Document) => doc.docNumber),
      distinctUntilChanged()
    );

    this.documentLocale$ = validDocument$.pipe(
      map((doc: Document) => doc.settings.locale),
      distinctUntilChanged()
    );

    this.documentOverallState$ = validDocument$.pipe(
      map((doc: Document) => doc.overallState),
      distinctUntilChanged()
    );

    this.documentPayments$ = validDocument$.pipe(
      map((doc: Document) => doc.payments),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentRevisionId$ = validDocument$.pipe(
      map((doc: Document) => doc.revisionId),
      distinctUntilChanged()
    );

    this.sourceDocumentPreview$ = validDocument$.pipe(
      filter((doc: Document) => doc.documentType === DocumentTypesEnum.invoice),
      map((doc: Document) => this._utilsService.getConvertedDoc(doc)),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.linkedDocumentPreview$ = validDocument$.pipe(
      filter(
        (doc: Document) => doc.documentType === DocumentTypesEnum.estimate
      ),
      map((doc: Document) => this._utilsService.getConvertedDoc(doc)),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentTrackingObject$ = validDocument$.pipe(
      map((doc: Document) =>
        this._trackingObjectBuilder.buildDocument(
          doc.id,
          doc.localId,
          doc.docNumber,
          doc.overallState,
          doc.revisionId,
          doc.documentType
        )
      ),
      distinctUntilChanged((a, b) => isEqual(a, b))
    );

    this.deposit$ = validDocument$.pipe(
      map((doc: Document) => doc.deposit),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.underwritingUrl$ = this._settingsDataStore.getUnderwritingUrl();

    this.companySettings$ = this._settingsDataStore.settings$.pipe(
      map((settings: Settings) => settings.companySettings),
      shareReplay(1)
    );

    this.emailDefaults$ = this.companySettings$.pipe(
      map((companySettings: CompanySettings) => companySettings.emailDefaults),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.paymentsSettings$ = this.companySettings$.pipe(
      map((companySettings: CompanySettings) => companySettings.payments),
      shareReplay(1)
    );

    this.documentPaymentUrl$ = combineLatest([
      validDocument$,
      this.paymentsSettings$,
    ]).pipe(
      map(([document, paymentsSettings]: [Document, PaymentsSettings]) => {
        return paymentsSettings.cardPaymentsEnabled() &&
          !document.settings.cardPaymentsDisabled
          ? document.links.electronicPaymentUrl
          : null;
      }),
      distinctUntilChanged()
    );

    this.showBankTransfer$ = combineLatest([
      this.documentSettings$,
      this.paymentsSettings$,
      this.documentFeatureName$,
    ]).pipe(
      map(
        ([documentSettings, paymentsSettings, documentFeatureName]: [
          DocumentPresetSettings,
          PaymentsSettings,
          string
        ]) =>
          this.enablePayments &&
          this.enableBankTransfer &&
          this.featureToggles.isAllAccess(documentFeatureName) &&
          !paymentsSettings.achDebit.isIneligible() &&
          paymentsSettings.achDebit.currencyCode ===
            documentSettings.currencyCode
      ),
      distinctUntilChanged()
    );

    const paymentsSettingsDocumentFeatureName$ = combineLatest([
      this.paymentsSettings$,
      this.documentFeatureName$,
    ]).pipe(shareReplay(1));

    this.showI2gMoney = this.featureToggles.isAllAccess(
      Toggles.KEYS.INVOICE2GO_MONEY
    );

    this.showCardPayments$ = paymentsSettingsDocumentFeatureName$.pipe(
      map(
        ([paymentsSettings, documentFeatureName]: [
          PaymentsSettings,
          string
        ]) => {
          const stripe = paymentsSettings.cardPayments.stripe;

          return (
            this.enablePayments &&
            this.featureToggles.isAllAccess(documentFeatureName) &&
            !(stripe && stripe.isIneligible()) &&
            (this.enableCardPayments ||
              (this.featureToggles.isForbidden(Toggles.KEYS.CARD_PAYMENTS) &&
                stripe.isEnabled()))
          );
        }
      ),
      distinctUntilChanged()
    );

    this.showPaypalEc$ = paymentsSettingsDocumentFeatureName$.pipe(
      map(
        ([paymentsSettings, documentFeatureName]: [PaymentsSettings, string]) =>
          this.enablePayments &&
          this.featureToggles.isAllAccess(documentFeatureName) &&
          this.enablePaypal
      ),
      distinctUntilChanged()
    );

    this.paypalEcStatus$ = this.paymentsSettings$.pipe(
      map((paymentSettings) => paymentSettings.paypalEcStatus)
    );

    this.i2gMoneyEnabled$ = combineLatest([
      this.documentSettings$,
      this._settingsDataStore.i2gMoney$,
    ]).pipe(
      map(
        ([documentSettings, i2gMoney]: [DocumentPresetSettings, I2gMoney]) =>
          !documentSettings.bankTransferDisabled &&
          i2gMoney?.context?.settings.allowed_by_partner &&
          i2gMoney?.context?.settings.enabled_by_user
      ),
      distinctUntilChanged()
    );

    this.bankTransferEnabled$ = combineLatest([
      this.documentSettings$,
      this.paymentsSettings$,
    ]).pipe(
      map(
        ([documentSettings, paymentsSettings]: [
          DocumentPresetSettings,
          PaymentsSettings
        ]) =>
          !documentSettings.bankTransferDisabled &&
          paymentsSettings.bankTransferEnabled()
      ),
      distinctUntilChanged()
    );

    const documentPaymentsSettings$ = combineLatest([
      this.documentSettings$,
      this.paymentsSettings$,
    ]).pipe(shareReplay(1));

    this.cardPaymentsEnabled$ = documentPaymentsSettings$.pipe(
      map(
        ([documentSettings, paymentsSettings]: [
          DocumentPresetSettings,
          PaymentsSettings
        ]) =>
          !documentSettings.cardPaymentsDisabled &&
          paymentsSettings.cardPaymentsEnabled()
      ),
      distinctUntilChanged()
    );

    this.paypalEcEnabled$ = documentPaymentsSettings$.pipe(
      map(
        ([documentSettings, paymentsSettings]: [
          DocumentPresetSettings,
          PaymentsSettings
        ]) =>
          !documentSettings.paypalEcDisabled && paymentsSettings.paypalEcEnabled
      ),
      distinctUntilChanged()
    );

    this.remindersSettings$ = this.companySettings$.pipe(
      map(
        (companySettings: CompanySettings) => companySettings.paymentReminders
      ),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.addDepositForFuture$ = this.companySettings$.pipe(
      distinctUntilChanged((a, b) => a && a.equals(b)),
      withLatestFrom(this.documentType$),
      map(
        ([companySettings, documentType]: [
          CompanySettings,
          DocumentTypesEnum
        ]) => {
          const previousValue = companySettings.depositToggle[documentType];

          if (previousValue) {
            return previousValue;
          } else {
            const depositToggleValueKeys = DepositToggleValue.KEYS;
            const depositTogglePayload =
              this.featurePayloads &&
              this.featurePayloads.get(Toggles.KEYS.DEPOSIT_TOGGLE);
            const documentToggleValue =
              depositTogglePayload && depositTogglePayload.get(documentType);

            return !isUndefined(documentToggleValue)
              ? new DepositToggleValue({
                  [depositToggleValueKeys.VALUE]: 50,
                  [depositToggleValueKeys.TOGGLE_STATE]: documentToggleValue,
                })
              : null;
          }
        }
      ),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.clientName$ = this.documentBilling$.pipe(
      map((billing: Billing) => billing.referenceId),
      filter((x) => !!x),
      flatMap((clientId: string) =>
        this._clientDataDispatcher.getEntityById(clientId)
      ),
      map((client: Client) => client.billingName)
    );

    // from attachment store: used to save the uploading files from file service
    // used for both image upload and file attachment upload
    this.attachments$ = this._attachmentsStore.files$;

    // from document schema and saved in the server
    this.fileAttachment$ = validDocument$.pipe(
      map((doc: Document) => doc.fileAttachments),
      distinctUntilChanged()
    );
  }

  ngAfterViewInit() {
    this._trackingDispatcher.trackPage(this.trackingScreenName);
  }

  onMessageUpdated(messageDeliveryData: IMessageDeliveryData) {
    this._messageDeliveryData = messageDeliveryData;

    this.disableSendButton = !messageDeliveryData.valid;

    this._changeDetectorRef.markForCheck();
  }

  handleRequiredDeposit(): Observable<Document> {
    this._dialogService.openDialog({ type: updateDepositRequestDialog });

    return this._dialogService.dialogTriggers$.pipe(
      filter(
        ({ type, action }: IDialogTrigger) =>
          type === updateDepositRequestDialog &&
          action === DialogActionsEnum.close
      ),
      map(({ data }: IDialogTrigger) => data.result),
      take(1),
      filter((result) => isBoolean(result)),
      flatMap((updateDepositRequest: boolean) => {
        if (updateDepositRequest) {
          this._dialogService.openDialog({ type: depositRequestDialog });
          return null;
        }

        return this.documentDataDispatcher.removeDepositInEditing(false);
      })
    );
  }

  handleUpdateClientEmail(
    document: Document,
    clientId: string,
    emails: string
  ): Observable<Document> {
    const newEmails = List(this._emailService.tokenizeEmails(emails))
      .toSet()
      .toList();

    this._dialogService.openDialog({ type: updateClientEmailDialog });

    return this._dialogService.dialogTriggers$.pipe(
      takeUntil(this._destroyed),
      filter(
        ({ action, type }: IDialogTrigger) =>
          action === DialogActionsEnum.close && type === updateClientEmailDialog
      ),
      take(1),
      flatMap(({ data }: IDialogTrigger) => {
        const { result }: IConfirmDialogResult = data;

        return result
          ? this._clientDataDispatcher.getEntityById(clientId).pipe(
              flatMap((client: Client) => {
                const newClient = this._documentSendBuilder.buildClientWithNewEmails(
                  client,
                  newEmails
                );

                this._clientDataDispatcher.setEditingEntity(newClient);

                return this._clientDataDispatcher.saveEditingEntity();
              }),
              tap(() => this._clientDataDispatcher.resetEditingEntity()),
              map(() =>
                this._documentSendBuilder.buildDocumentWithNewEmails(
                  document,
                  newEmails
                )
              )
            )
          : of(document);
      })
    );
  }

  sendDocument() {
    this.documentDataStore
      .getValidEditingEntity()
      .pipe(
        filter((document: Document) => document.referencesPopulated()),
        take(1),

        // handle invalid deposit request before sending
        flatMap(
          (document: Document): Observable<Document> => {
            if (
              this.enableDeposits &&
              document.depositAmountLargerThanOutstandingBalance()
            ) {
              return this.handleRequiredDeposit();
            }

            return of(document);
          }
        ),
        filter((x) => !!x),

        // update client email if empty
        flatMap(
          (document: Document): Observable<Document> => {
            if (!document.billingEmail && document.billing.reference) {
              return this.handleUpdateClientEmail(
                document,
                document.billing.referenceId,
                this._messageDeliveryData.to
              );
            }

            return of(document);
          }
        ),

        // save document before sending so server has the latest document
        tap(() => this._dialogService.openDialog({ type: sendingDialog })),
        flatMap((document: Document) => {
          this.documentDataDispatcher.setEditingEntity(document);

          return this.documentDataDispatcher.saveEditingEntity();
        }),

        // send document
        flatMap(
          (document: Document): Observable<Document> =>
            this.documentDataDispatcher.sendDocument(
              document,
              this.enableBankTransfer,
              this.enableCardPayments,
              this.enablePaypal,
              this._messageDeliveryData
            )
        ),
        finalize(() =>
          this._dialogService.closeDialog({ type: sendingDialog })
        ),
        takeUntil(this._destroyed)
      )
      .subscribe(
        (document: Document) => {
          if (this.jobId && this.clientId) {
            this._jobDataDispatcher.addEstimate(document.id, this.jobId);

            return this._navigationService.goTo([
              CLIENTS_MASTER_ROUTE,
              this.clientId,
              CLIENTS_JOBS_ROUTE,
              this.jobId,
            ]);
          }

          this._documentDetailService.triggerJobDialog(this.toggles, document);

          if (document.documentType === DocumentTypesEnum.invoice) {
            const filterRoute: string =
              document.states.overall === DocumentOverallStatesEnum.fully_paid
                ? INVOICES_PAID_ROUTE
                : INVOICES_UNPAID_ROUTE;

            this._navigationService.goTo([this.masterRoute, filterRoute]);
            return;
          }

          this._navigationService.goTo([this.masterRoute]);
        },
        ([status, errorBody, error]) => {
          if (
            status === HttpStatus.NOT_FOUND &&
            errorBody &&
            errorBody.code === ERRORS.PDF_RENDERING_FAILED
          ) {
            this._dialogService.openDialog({ type: pdfRerenderDialog });

            return;
          }

          this._globalBannerService.showSystemErrorBanner(error);
        }
      );
  }

  updateDocumentContent(documentContentProps: Map<string, any>) {
    this.documentDataDispatcher.setEditingEntityContent(documentContentProps);
  }

  updateSettingsContent(settingsContentProps: Map<string, any>) {
    this._settingsDataDispatcher.updateSettings(settingsContentProps);
    this._settingsDataDispatcher.saveCurrentSettingsToNetwork();
  }

  onUpdateRecurringSchedule(payload: Map<string, any>) {
    if (this.documentDataDispatcher instanceof InvoiceDataDispatcher) {
      const invoiceDataDispatcher = this
        .documentDataDispatcher as InvoiceDataDispatcher;

      this.documentId$
        .pipe(takeUntil(this._destroyed))
        .subscribe((documentId: string) => {
          if (payload) {
            invoiceDataDispatcher.updateSchedule(documentId, payload);
          } else {
            invoiceDataDispatcher.deleteSchedule(documentId);
          }
        });
    }
  }

  setInTmpCache() {
    this.documentDataStore
      .getValidEditingEntity()
      .pipe(take(1), takeUntil(this._destroyed))
      .subscribe((editingDocument: Document) => {
        this.documentDataDispatcher.setInTmpCache(
          editingDocument.id,
          editingDocument
        );
      });
  }

  ngOnDestroy() {
    this._dialogService.closeDialog({ type: updateClientEmailDialog });
    this._dialogService.closeDialog({ type: sendingDialog });
    this._dialogService.closeDialog({ type: pdfRerenderDialog });
    this._dialogService.closeDialog({ type: updateDepositRequestDialog });
    this._dialogService.closeDialog({ type: depositRequestDialog });

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
    this._attachmentsStore.clearAttachments();
  }
}
